import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import api from '@/services/api';
import { getApiBaseUrl } from '@/config/environment';

import { UserIcon, LockClosedIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import PWAInstallButton from '@/components/PWAInstallButton';

const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    remember_me: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Vérifier si l'utilisateur est déjà connecté (avec protection contre les boucles)
  React.useEffect(() => {
    const token = localStorage.getItem('access_token');
    const skipRedirect = sessionStorage.getItem('skip-login-redirect');
    
    if (token && !skipRedirect) {
      // Vérifier si le token est valide en faisant un appel simple
      axios.get(`${getApiBaseUrl()}/auth/user-profile/`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(() => {
        // Token valide, rediriger vers le dashboard
        window.location.href = '/dashboard';
      }).catch(() => {
        // Token invalide, supprimer et continuer
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('activeSiteCode');
        localStorage.removeItem('activeSiteInfo');
      });
    } else if (skipRedirect) {
      // Nettoyer le flag de session
      sessionStorage.removeItem('skip-login-redirect');
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      // Utiliser le service API configuré
      const response = await api.post('/auth/login/', {
        username: formData.username,
        password: formData.password
      });

      if (response.data.success && response.data.data) {
        const data = response.data.data;
        
        // Vérifier si la 2FA est requise
        if (data.two_factor_required) {
          setSuccess('Code de vérification envoyé par email. Veuillez vérifier votre boîte email.');
          // Redirection vers la page 2FA
          setTimeout(() => {
            window.location.href = '/2fa';
          }, 2000);
        } else if (data.access_token) {
          // Stocker les tokens
          localStorage.setItem('access_token', data.access_token);
          localStorage.setItem('refresh_token', data.refresh_token);
          
          setSuccess('Connexion réussie ! Redirection en cours...');
          
          // Marquer qu'on ne doit pas rediriger automatiquement depuis la page de login
          sessionStorage.setItem('skip-login-redirect', 'true');
          
          // Redirection après un court délai
          setTimeout(() => {
            window.location.href = '/dashboard';
          }, 2000);
        }
      } else {
        throw new Error(response.data.message || 'Réponse de connexion invalide');
      }
    } catch (err: any) {
      // Gestion d'erreur améliorée
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Erreur de connexion. Veuillez vérifier vos identifiants et réessayer.');
      }
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="min-h-screen gradient-bg flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Bouton d'installation PWA */}
        <PWAInstallButton />
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Logo Pontcita */}
          <div className="text-center mb-8">
            <div className="w-24 h-24 mx-auto mb-6 relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-forest-green/20 to-bright-orange/20 rounded-2xl blur-sm group-hover:blur-md transition-all duration-300"></div>
              <div className="relative w-full h-full rounded-2xl shadow-lg border border-[#0d6938]/20 overflow-hidden group-hover:shadow-xl transition-all duration-300 group-hover:scale-105 p-2" style={{backgroundColor: '#0d6938'}}>
                <img 
                  src="images/logo-pontcita.png" 
                  alt="Logo Pontcita" 
                  className="w-full h-full object-contain drop-shadow-sm"
                />
              </div>
            </div>
            <h1 className="text-2xl font-poppins font-semibold text-dark-black">Plateforme de gestion du Pont Bascule</h1>
            <p className="text-sm text-gray-600 mt-2">Connectez-vous à votre compte</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-600">{success}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-dark-black mb-2">
                Nom d'utilisateur
              </label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bright-orange focus:border-transparent transition-all"
                  placeholder="Votre identifiant"
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-black mb-2">
                Mot de passe
              </label>
              <div className="relative">
                <LockClosedIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bright-orange focus:border-transparent transition-all"
                  placeholder="Votre mot de passe"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="remember_me"
                  checked={formData.remember_me}
                  onChange={handleInputChange}
                  className="rounded border-gray-300 text-bright-orange focus:ring-bright-orange"
                  disabled={isLoading}
                />
                <span className="ml-2 text-sm text-gray-600">Se souvenir de moi</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm text-bright-orange hover:underline"
              >
                Mot de passe oublié ?
              </Link>
            </div>


            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary text-white py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Connexion...
                </div>
              ) : (
                <>
                  <LockClosedIcon className="inline-block h-5 w-5 mr-2" />
                  Se connecter
                </>
              )}
            </button>
          </form>


          <div className="mt-6 text-center text-xs text-gray-500">
            <div className="flex items-center justify-center gap-2 mb-2">
              <span>Un produit du</span>
              <img 
                src="images/cca-logo.svg" 
                alt="CCA Logo" 
                className="h-4 w-auto"
              />
            </div>
            <div>Version 2.1.0 - © 2025 CCA - Conseil du Coton et de l'Anacarde</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;